import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'

import { useQueryState } from '@/hooks/use-query-state'
import { queryKeys } from '@/lib/query-keys'
import { cashApi } from '@/services/http/cash'
import { pickFirm, useCashFirmStore } from '@/stores/cash-firm-store'

/** Konsolide görünümün firma anahtarı (backend `consolidation.GRUP`). */
export const GROUP_FIRM = 'GRUP'

/**
 * Seçili firma: adres çubuğundaki `?firma=` önceliklidir; yoksa son seçilen firma (sayfalar arası
 * taşınır); o da yoksa ya da yetkisizse kapsamdaki ilk firma.
 */
export function useCashFirm({ allowGroup = false }: { allowGroup?: boolean } = {}) {
  const query = useQueryState()
  const all = useQuery({ queryKey: queryKeys.cash.firms(), queryFn: cashApi.firms })
  const remembered = useCashFirmStore((state) => state.firm)
  const remember = useCashFirmStore((state) => state.remember)
  const list = (all.data ?? []).filter((f) => allowGroup || f.key !== GROUP_FIRM)
  const wanted = query.get('firma')
  const keys = list.map((f) => f.key)
  const firm = pickFirm(wanted, remembered, keys)
  const urlFirm = wanted && keys.includes(wanted) ? wanted : undefined
  useEffect(() => {
    if (urlFirm) remember(urlFirm)
  }, [urlFirm, remember])
  const setFirm = useCallback(
    (value: string) => {
      remember(value)
      query.set({ firma: value })
    },
    [query, remember],
  )
  // İzin firma başınadır (backend `cash/scope.py`): bir firmada planlayan öbüründe yalnız görür.
  const granted = list.find((f) => f.key === firm)?.permissions
  const can = (...permissions: string[]) => permissions.some((p) => granted?.includes(p) ?? false)
  return { firms: { ...all, data: all.data ? list : undefined }, firm, setFirm, can }
}

export function useInvalidateCash() {
  const client = useQueryClient()
  return useCallback(() => client.invalidateQueries({ queryKey: ['cash'] }), [client])
}

/** Menüdeki Bu Hafta rozeti: seçili firmanın bekleyen iş maddesi sayısı (ekranla aynı sorgu). */
export function useCashTodoCount(): number | undefined {
  const { firm } = useCashFirm()
  const todo = useQuery({
    queryKey: queryKeys.cash.todo(firm ?? ''),
    queryFn: () => cashApi.todo(firm!),
    enabled: !!firm,
    refetchInterval: 5 * 60_000,
  })
  return todo.data?.length
}
